import { useState, useEffect, useCallback, useRef } from 'react';
import { saveDeviceToken } from '../api/device/saveDeviceToken';
import { getDeviceInfo, getCurrentLocation, getLocationInfo } from '../utils/deviceInfo';

export function useDeviceToken() {
    const [isRegistered, setIsRegistered] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isRegistering, setIsRegistering] = useState(false);
    const registerDeviceRef = useRef(null);

    const updateFirebaseToken = useCallback(async (firebaseToken) => {
        if (!isRegistered) {
            if (registerDeviceRef.current) {
                await registerDeviceRef.current(firebaseToken);
            }
            return;
        }

        try {
            const deviceInfo = await getDeviceInfo();
            deviceInfo.firebase_token = firebaseToken || ''; // Ensure it's a string
            
            // Force update even if device is already registered (for token updates)
            await saveDeviceToken(deviceInfo, true);
        } catch (err) {
            setError(err.message);
        }
    }, [isRegistered]);

    const registerDevice = useCallback(async (firebaseToken = null) => {
        // If device is already registered and token is provided, update token instead
        if (isRegistered && firebaseToken) {
            await updateFirebaseToken(firebaseToken);
            return;
        }
        
        // Prevent multiple simultaneous registrations
        if (isRegistering || isRegistered) {
            return;
        }

        // Check if registration was recently attempted and failed
        const lastAttempt = localStorage.getItem('device_registration_last_attempt');
        const now = Date.now();
        if (lastAttempt && (now - parseInt(lastAttempt)) < 60000) { // 1 minute cooldown
            return;
        }

        setIsRegistering(true);
        setIsLoading(true);
        setError(null);

        // Mark attempt time
        localStorage.setItem('device_registration_last_attempt', now.toString());

        try {
            // Get basic device info (this will try to get FCM token if available)
            const deviceInfo = await getDeviceInfo();
            
            // Override firebase token if explicitly provided, otherwise use the one from getDeviceInfo()
            // getDeviceInfo() already tries to get the token, so we only override if a token is explicitly passed
            if (firebaseToken) {
                deviceInfo.firebase_token = firebaseToken;
            }
            // If firebaseToken is null/undefined, keep the token from getDeviceInfo() (which might be empty string if unavailable)
            // Ensure it's always a string, not null
            if (!deviceInfo.firebase_token) {
                deviceInfo.firebase_token = '';
            }

            // Try to get location
            try {
                const location = await getCurrentLocation();
                if (location) {
                    const locationInfo = await getLocationInfo(location.latitude, location.longitude);
                    deviceInfo.location = {
                        latitude: location.latitude,
                        longitude: location.longitude,
                        country: locationInfo.country,
                        locality: locationInfo.locality
                    };
                }
            } catch (locationError) {
                // Continue without location
            }

            // Save to backend
            await saveDeviceToken(deviceInfo);
            
            // Mark as registered
            localStorage.setItem('device_registered', 'true');
            setIsRegistered(true);
        } catch (err) {
            const errorMessage = err?.message || err?.toString() || '';
            setError(errorMessage);
            
            // Check if it's a server error (500) or known backend bug
            if (errorMessage.includes('500') || errorMessage.includes('datetime') || errorMessage.includes('Quota exceeded')) {
                // Known backend bug - silently handle
                localStorage.setItem('device_registration_disabled', 'true');
                // Re-enable after 5 minutes
                setTimeout(() => {
                    localStorage.removeItem('device_registration_disabled');
                }, 5 * 60 * 1000);
            }
            
            // Don't mark as registered if there was an error
            setIsRegistered(false);
        } finally {
            setIsLoading(false);
            setIsRegistering(false);
        }
    }, [isRegistering, isRegistered, updateFirebaseToken]);

    // Update ref when registerDevice changes
    useEffect(() => {
        registerDeviceRef.current = registerDevice;
    }, [registerDevice]);

    // Check if device is already registered on mount
    useEffect(() => {
        const isDeviceRegistered = localStorage.getItem('device_registered') === 'true';
        setIsRegistered(isDeviceRegistered);
        
        // Auto-register if not registered and user is logged in
        if (!isDeviceRegistered && localStorage.getItem('access_token')) {
            registerDevice();
        }
    }, [registerDevice]);

    const resetDeviceRegistration = () => {
        localStorage.removeItem('device_registered');
        localStorage.removeItem('device_id');
        setIsRegistered(false);
        setIsRegistering(false);
        setError(null);
    };

    const testDeviceRegistration = async () => {
        // Test with minimal data to see what backend expects
        const testData = {
            device_id: 'test_device_123',
            firebase_token: '', // Empty string instead of null
            platform: 'web',
            os_version: 'unknown',
            app_version: '1.0.0',
            device_model: 'unknown',
            timezone: 'UTC',
            language: 'en'
        };
        
        try {
            await saveDeviceToken(testData);
        } catch (error) {
            // Test registration failed
        }
    };

    return {
        isRegistered,
        isLoading,
        error,
        registerDevice,
        updateFirebaseToken,
        resetDeviceRegistration,
        testDeviceRegistration
    };
}
